import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, IconButton, Badge, useMediaQuery, useTheme,
  Dialog, DialogContent, TextField, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  CalendarCheck,
  PawPrint,
  HandHelping,
  ArrowLeftRight,
  MessageCircle,
  Star,
  QrCode,
  BarChart3,
  Coins,
  LogOut,
  Settings,
  Search,
  Bell,
  Menu,
  Package,
  Flag,
  ShieldAlert,
  Banknote,
  SlidersHorizontal,
  Smartphone,
  ChevronDown,
} from 'lucide-react';
import api from '../config/api';

const drawerWidth = 260;
const ff = "'Poppins', sans-serif";

// Dashboard sits on its own at the top; everything else is grouped into
// single-open accordion sections (opening one collapses the others).
const STANDALONE = [
  { text: 'Dashboard', icon: LayoutDashboard, path: '/' },
];

const NAV_GROUPS = [
  {
    label: 'Users & Sitters', icon: Users, items: [
      { text: 'Users', icon: Users, path: '/users' },
      { text: 'Sitters', icon: UserCheck, path: '/sitters' },
    ],
  },
  {
    label: 'Bookings & Pets', icon: CalendarCheck, items: [
      { text: 'Bookings', icon: CalendarCheck, path: '/bookings' },
      { text: 'Pets', icon: PawPrint, path: '/pets' },
      { text: 'Services', icon: HandHelping, path: '/services' },
      { text: 'Linked QR', icon: QrCode, path: '/linked-qr' },
      { text: 'QR Codes', icon: Package, path: '/qr-codes' },
    ],
  },
  {
    label: 'Finance', icon: Coins, items: [
      { text: 'Transactions', icon: ArrowLeftRight, path: '/transactions' },
      { text: 'Coins', icon: Coins, path: '/coins' },
      { text: 'Withdrawals', icon: Banknote, path: '/withdrawals' },
      { text: 'Payout Settings', icon: SlidersHorizontal, path: '/payout-settings' },
    ],
  },
  {
    label: 'Engagement', icon: MessageCircle, items: [
      { text: 'Chats', icon: MessageCircle, path: '/chats' },
      { text: 'Reviews', icon: Star, path: '/reviews' },
      { text: 'User Reports', icon: ShieldAlert, path: '/user-reports' },
    ],
  },
  {
    label: 'System', icon: Settings, items: [
      { text: 'Reports', icon: BarChart3, path: '/reports' },
      { text: 'App Version', icon: Smartphone, path: '/app-version' },
      { text: 'App Config', icon: Smartphone, path: '/app-config' },
      { text: 'Feature Flags', icon: Flag, path: '/feature-flags' },
    ],
  },
];

// Flat list for the page-title lookup and the search dialog.
const ALL_MENU_ITEMS = [...STANDALONE, ...NAV_GROUPS.flatMap((g) => g.items)];

const pageSubtitles = {
  '/': 'Your daily app analytics',
  '/users': 'Manage all platform users',
  '/bookings': 'Track and manage bookings',
  '/pets': 'All registered pets',
  '/services': 'Service configurations',
  '/transactions': 'Coin transactions history',
  '/chats': 'User conversations',
  '/reviews': 'User feedback and ratings',
  '/user-reports': 'Reported users & content moderation',
  '/linked-qr': 'QR codes linked to pets',
  '/qr-codes': 'Generate and manage QR codes',
  '/reports': 'Platform analytics & reports',
  '/coins': 'Coin management',
  '/withdrawals': 'Sitter cash-out requests',
  '/payout-settings': 'Cash-out fees, limits & rules',
  '/app-version': 'Force-update & version control',
  '/app-config': 'Store links, Amazon & support — fetched by the app',
  '/feature-flags': 'Runtime settings + provider switches',
  '/analytics': 'Platform analytics',
  '/settings': 'Platform settings',
  '/sitters': 'Manage pet sitters',
};

export default function Layout({ children, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Single-open accordion: which nav group is expanded (by label, or null).
  const [openGroup, setOpenGroup] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data?.data?.count || res.data?.count || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Auto-open the group containing the current route on navigation (no
  // persistence — always reflects where you are).
  useEffect(() => {
    const g = NAV_GROUPS.find((grp) => grp.items.some((it) => isActive(it.path)));
    setOpenGroup(g ? g.label : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const currentPage = ALL_MENU_ITEMS.find(item => isActive(item.path));
  const pageTitle = currentPage?.text || location.pathname.slice(1).replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase()) || 'Dashboard';
  const pageSubtitle = pageSubtitles[location.pathname] || '';

  // Renders a single nav row (used for the standalone Dashboard and for the
  // items inside each expanded group; group children are indented).
  const renderNavItem = (item, indented) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    return (
      <Box
        key={item.text}
        onClick={() => {
          navigate(item.path);
          if (isMobile) setMobileOpen(false);
        }}
        sx={{
          height: 40,
          pl: indented ? 4.75 : 2,
          pr: 2,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          background: active ? '#EBF8FF' : 'transparent',
          transition: 'all 0.15s ease',
          '&:hover': { background: active ? '#EBF8FF' : '#F7FAFC' },
        }}
      >
        <Icon size={indented ? 16 : 18} strokeWidth={active ? 2.2 : 1.8} color={active ? '#32A6D8' : '#A0AEC0'} />
        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#32A6D8' : '#718096', lineHeight: 1 }}>
          {item.text}
        </Box>
      </Box>
    );
  };

  const sidebar = (
    <Box sx={{
      width: drawerWidth,
      height: '100%',
      background: '#FAFBFC',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid #F0F0F0',
    }}>
      {/* Logo */}
      <Box sx={{ px: 3, pt: 3.5, pb: 2 }}>
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            px: 1,
          }}
        >
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #32A6D8 0%, #005A64 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <PawPrint size={20} color="white" strokeWidth={2.5} />
          </Box>
          <Box>
            <Box sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              color: '#1A202C',
              lineHeight: 1.2,
              letterSpacing: -0.3,
            }}>
              OkTreat
            </Box>
            <Box sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 10,
              fontWeight: 500,
              color: '#A0AEC0',
              lineHeight: 1,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}>
              Admin Panel
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ mx: 3, mb: 1.5, borderBottom: '1px solid #EDF2F7' }} />

      {/* Nav */}
      <Box sx={{
        px: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        overflowY: 'auto',
        py: 0.5,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { background: '#E2E8F0', borderRadius: 4 },
      }}>
        {/* Standalone items (Dashboard) */}
        {STANDALONE.map((item) => renderNavItem(item, false))}

        {/* Accordion groups — single-open */}
        {NAV_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          const groupActive = group.items.some((it) => isActive(it.path));
          const open = openGroup === group.label;
          return (
            <Box key={group.label}>
              <Box
                onClick={() => setOpenGroup(open ? null : group.label)}
                sx={{
                  height: 42,
                  px: 2,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': { background: '#F7FAFC' },
                }}
              >
                <GroupIcon size={18} strokeWidth={groupActive ? 2.2 : 1.8} color={groupActive ? '#32A6D8' : '#A0AEC0'} />
                <Box sx={{ flex: 1, fontFamily: ff, fontSize: 13, fontWeight: groupActive ? 600 : 500, color: groupActive ? '#32A6D8' : '#718096', lineHeight: 1 }}>
                  {group.label}
                </Box>
                <ChevronDown
                  size={16}
                  color="#A0AEC0"
                  style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}
                />
              </Box>
              {open && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mt: '2px' }}>
                  {group.items.map((item) => renderNavItem(item, true))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Logout */}
      <Box sx={{ px: 2, pb: 3, pt: 1 }}>
        <Box
          onClick={onLogout}
          sx={{
            height: 42,
            px: 2,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            background: '#FFF5F5',
            transition: 'all 0.15s ease',
            '&:hover': { background: '#FED7D7' },
          }}
        >
          <LogOut size={18} strokeWidth={1.8} color="#E53E3E" />
          <Box sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: '#E53E3E',
          }}>
            Log Out
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FFFFFF' }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth, border: 'none' },
        }}
      >
        {sidebar}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: drawerWidth, border: 'none' },
        }}
        open
      >
        {sidebar}
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: `${drawerWidth}px` },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          background: '#FFFFFF',
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 2, md: 3.5 },
          pb: 1,
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ display: { md: 'none' }, p: 0.5 }}
              >
                <Menu size={22} color="#1A202C" />
              </IconButton>
              <Box sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 22,
                fontWeight: 600,
                color: '#1A202C',
                lineHeight: 1.3,
              }}>
                {pageTitle}
              </Box>
            </Box>
            {pageSubtitle && (
              <Box sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 13,
                fontWeight: 400,
                color: '#A0AEC0',
                mt: 0.25,
                ml: { xs: 0, md: 0 },
              }}>
                {pageSubtitle}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {[
              { icon: <Settings size={18} strokeWidth={1.8} color="#4A5568" />, onClick: () => navigate('/settings'), tip: 'Settings' },
              { icon: <Search size={18} strokeWidth={1.8} color="#4A5568" />, onClick: () => setSearchOpen(true), tip: 'Search' },
              {
                icon: (
                  <Badge badgeContent={unreadCount} color="error" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16 } }}
                  >
                    <Bell size={18} strokeWidth={1.8} color="#4A5568" />
                  </Badge>
                ),
                onClick: () => navigate('/chats'),
                tip: 'Notifications',
              },
            ].map((btn, i) => (
              <IconButton
                key={i}
                onClick={btn.onClick}
                title={btn.tip}
                sx={{
                  width: 40,
                  height: 40,
                  background: '#F7FAFC',
                  '&:hover': { background: '#EDF2F7' },
                }}
              >
                {btn.icon}
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 4, pt: 1.5 }}>
          {children}
        </Box>
      </Box>

      {/* Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', mt: -10, background: '#FAFBFC' },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: "'Poppins', sans-serif", fontSize: 15,
                borderRadius: 0, border: 'none',
                '& fieldset': { border: 'none' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color="#A0AEC0" />
                </InputAdornment>
              ),
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); }
            }}
          />
          <Box sx={{ borderTop: '1px solid #EDF2F7' }}>
            <List dense sx={{ py: 1 }}>
              {ALL_MENU_ITEMS
                .filter(item => !searchQuery || item.text.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <ListItemButton
                      key={item.path}
                      onClick={() => { navigate(item.path); setSearchOpen(false); setSearchQuery(''); }}
                      sx={{
                        mx: 1, borderRadius: '8px', mb: 0.25,
                        '&:hover': { background: '#EBF8FF' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon size={16} color="#718096" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontFamily: "'Poppins', sans-serif", fontSize: 13, color: '#2D3748',
                        }}
                        secondary={pageSubtitles[item.path]}
                        secondaryTypographyProps={{
                          fontFamily: "'Poppins', sans-serif", fontSize: 11, color: '#A0AEC0',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              {searchQuery && ALL_MENU_ITEMS.filter(item => item.text.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                <Box sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, color: '#A0AEC0', textAlign: 'center', py: 3 }}>
                  No pages found
                </Box>
              )}
            </List>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
